import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

import { isNil } from "lodash";

import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade, Navigation } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import "swiper/css/navigation";

import ScaleLoader from "react-spinners/ScaleLoader";

/**
 * SocialPropertySwiper component displays Images with swiper on Listing Feed Page
 * @param {Object} props - Component props
 * @param {Object} props.property - Properties Details
 * @returns {JSX.Element} - Property Swiper for SinglePropertyFeed.
 */
const SocialPropertySwiper = ({ property }) => {
    const images = property?.image_urls;
    const statusIndicator = property?.status_indicator;
    const status = statusIndicator ? statusIndicator.toLowerCase() : null;
    const swiperRef = useRef(null);
    const [loadedImages, setLoadedImages] = useState(Array(images.length).fill(false));

    const handleImageLoad = (index) => {
        setLoadedImages((prev) => {
            const newLoaded = [...prev];
            newLoaded[index] = true;
            return newLoaded;
        });
    };

    const handleSlideChange = (swiper) => {
        localStorage.setItem(`swiper-index-${property.id}`, swiper.activeIndex);
    };

    const handleArrowClick = (e) => {
        e.stopPropagation();
    };

    return (
        <Swiper
            ref={swiperRef}
            modules={[EffectFade, Navigation]}
            loop={false}
            navigation={{
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
            }}
            data-cy="property-image-swiper"
            speed="400"
            effect="fade"
            className="property-image-swiper !tw-object-cover"
            onSlideChange={handleSlideChange}
        >
            {images.map((image, index) => (
                <SwiperSlide className="swiper-slide" key={index}>
                    <div
                        className="image-container"
                        data-fancybox={`gallery-${property.id}`}
                        data-src={image}
                    >
                        {statusIndicator && (
                            <span
                                className={clsx(
                                    "tw-absolute tw-top-[5%] tw-py-1 tw-px-4 tw-rounded-[500px] tw-z-[40] tw-right-[5%]",
                                    {
                                        "tw-bg-[#AAD880]": status === "active",
                                        "tw-bg-[#e7ac2d]": status === "contract",
                                        "tw-bg-[#494949] tw-text-white": status === "closed",
                                    }
                                )}
                            >
                                {statusIndicator}
                            </span>
                        )}
                        {(!loadedImages[index] || isNil(loadedImages[index])) && (
                            <div className="tw-flex !tw-justify-center !tw-items-center tw-h-full">
                                <ScaleLoader color="#63c19f" />
                            </div>
                        )}
                        <img
                            src={image}
                            alt={`Slide ${index}`}
                            onLoad={() => handleImageLoad(index)}
                            style={{ display: loadedImages[index] ? "block" : "none" }}
                            className="!tw-w-full md:!tw-w-auto tw-object-cover md:tw-object-contain tw-object-center tw-rounded-xl"
                        />
                    </div>
                </SwiperSlide>
            ))}
            <div className="swiper-button-prev !tw-left-5" onClick={handleArrowClick} />
            <div className="swiper-button-next !tw-right-5" onClick={handleArrowClick} />
        </Swiper>
    );
};

export default SocialPropertySwiper;

SocialPropertySwiper.propTypes = {
    property: PropTypes.object.isRequired,
};
